import React from "react";
import { PlayCircleOutlined } from "@ant-design/icons";
import { languageOptions } from "../../../utils";

const MetaPill = ({ label, value }) => (
  <span className="inline-flex w-fit max-w-full items-center gap-1.5 rounded-full bg-secondary px-3 py-1 text-xs text-gray-700">
    <span className="text-gray-400 shrink-0">{label}</span>
    <span className="font-medium text-gray-900 truncate">{value}</span>
  </span>
);

const VideoCard = ({
  video,
  videoDurations,
  playingVideoId,
  onVideoClick,
  onVideoLoad,
  onVideoPlayPause,
}) => {
  const isPlaying = playingVideoId === video._id;
  const languageLabel =
    languageOptions.find((opt) => opt.value === video.language)?.label ||
    "Not specified";

  const formattedDate = video.date_created
    ? new Date(video.date_created).toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      })
    : null;

  const handlePlayClick = (event) => {
    event.stopPropagation();
    const videoEl = document.getElementById(`video-${video._id}`);
    if (videoEl) {
      onVideoPlayPause(video._id, { current: videoEl });
    }
  };

  return (
    <div
      onClick={() => onVideoClick(video)}
      className="group flex h-full flex-col overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-md cursor-pointer transition-all duration-200 hover:-translate-y-1 hover:border-gray-300 hover:shadow-xl"
    >
      <div className="relative flex aspect-video items-center justify-center overflow-hidden bg-gray-900">
        <video
          id={`video-${video._id}`}
          src={video.url}
          className="max-h-full max-w-full object-contain"
          onLoadedMetadata={(e) => onVideoLoad(video._id, e)}
          onClick={handlePlayClick}
        />

        <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-black/10" />

        {!isPlaying && (
          <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
            <div className="flex h-14 w-14 items-center justify-center rounded-full border border-white/30 bg-white/15 backdrop-blur-sm transition-transform duration-200 group-hover:scale-110">
              <PlayCircleOutlined className="text-3xl text-white drop-shadow-md" />
            </div>
          </div>
        )}

        {formattedDate && (
          <div className="absolute left-3 top-3 rounded-lg bg-black/55 px-2.5 py-1 text-[11px] font-medium text-white backdrop-blur-sm">
            {formattedDate}
          </div>
        )}

        <div className="absolute bottom-3 right-3 rounded-lg bg-black/75 px-2 py-1 text-[11px] font-semibold tabular-nums text-white">
          {videoDurations[video._id] || "00:00"}
        </div>
      </div>

      <div className="flex flex-1 flex-col p-4">
        <h3 className="mb-0.5 mt-0 line-clamp-1 text-base font-semibold capitalize text-gray-900">
          {video.title}
        </h3>
        <p className="mb-0 mt-0 line-clamp-1 text-sm leading-relaxed text-gray-500">
          {video.description?.trim() || "No description provided."}
        </p>

        <div className="mt-3 -mx-4 flex flex-col items-start gap-2 px-4 pt-3">
          <MetaPill
            label="Associated to"
            value={video.organization?.name || "None"}
          />
          <MetaPill label="Language" value={languageLabel} />
          {video.interpreter?.user?.full_name && (
            <MetaPill
              label="Presenter"
              value={video.interpreter.user.full_name}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default VideoCard;
